import axios from 'axios'
import i18n from '@/i18n'

export async function listTemplates(apiBase) {
  const response = await axios.get(`${apiBase}/templates`)
  return response.data.data
}

export async function loadTemplate(apiBase, templateId) {
  const response = await axios.get(`${apiBase}/templates/${encodeURIComponent(templateId)}`)
  return response.data.data
}

export async function loadAd(apiBase, adId) {
  const response = await axios.get(`${apiBase}/ads/${encodeURIComponent(adId)}`)
  return response.data.data
}

// `databaseAdId`: a new ad's first save is the design of that "Let Us Design It" ad.
// `specAd`: a Leadbuster Spec Ad's start key (AD-254), sent with its first save only.
export async function saveAd(apiBase, adId, revision, configuration, pendingFiles, databaseAdId = null, specAd = null) {
  const entries = Object.entries(pendingFiles || {})
  const hasFiles = entries.length > 0
  const url = adId
    ? `${apiBase}/ads/${encodeURIComponent(adId)}`
    : `${apiBase}/ads`
  const designFor = !adId && databaseAdId ? databaseAdId : null
  const specFor = !adId && specAd ? specAd : null

  if (!hasFiles) {
    const response = adId
      ? await axios.put(url, { revision, configuration })
      : await axios.post(url, {
        configuration,
        ...(designFor ? { databaseAdId: designFor } : {}),
        ...(specFor ? { specAd: specFor } : {}),
      })
    return response.data.data
  }

  const body = new FormData()
  body.append('configuration', JSON.stringify(configuration))
  if (revision != null) body.append('revision', String(revision))
  if (designFor) body.append('databaseAdId', String(designFor))
  if (specFor) body.append('specAd', specFor)
  if (adId) body.append('_method', 'PUT')
  entries.forEach(([assetId, file]) => body.append(`assets[${assetId}]`, file))

  const response = await axios.post(url, body)
  return response.data.data
}

/** A designer's "Use third-party creative": the ad leaves this editor; the answer is where it opens. */
export async function switchToThirdPartyCreative(apiBase, adId) {
  const response = await axios.post(`${apiBase}/ads/${encodeURIComponent(adId)}/third-party-creative`)
  return response.data.data.redirect
}

export async function publishAd(apiBase, adId, revision, force = false) {
  const response = await axios.post(
    `${apiBase}/ads/${encodeURIComponent(adId)}/publish`,
    { revision, force },
  )
  return response.data.data
}

export async function askStudioAssistant(apiBase, payload, signal) {
  const response = await axios.post(`${apiBase}/assistant/messages`, payload, { signal })
  return response.data.data
}

// Stop: releases the server-side in-flight lock this request holds (see AssistantController).
export async function cancelStudioAssistant(apiBase, clientRequestId) {
  const response = await axios.post(`${apiBase}/assistant/cancel`, { clientRequestId })
  return response.data.data
}

// AD-133: one Places session per search — suggestions while typing, then the chosen place's
// details, both with the same `session` token, so Google bills them as one search.
export async function fetchAdvertiserSuggestions(apiBase, input, language, session, signal) {
  const response = await axios.get(`${apiBase}/places/autocomplete`, {
    params: { input, language, session },
    signal,
  })
  return Array.isArray(response?.data?.data) ? response.data.data : []
}

/** Whether the ad's website answers: { status: 'reachable' | 'unreachable' | 'invalid', url }. */
export async function checkWebsiteAvailability(apiBase, website, signal) {
  const response = await axios.get(`${apiBase}/website-availability`, { params: { website }, signal })
  return response.data.data
}

/** The chosen place as the advertiser (ResolvedAdvertiser): name, website, address, phone, category. */
export async function fetchAdvertiserDetails(apiBase, placeId, language, session, signal) {
  const response = await axios.get(`${apiBase}/places/details`, {
    params: { placeId, language, session },
    signal,
  })
  return response.data.data
}

// Fields as JSON, or with attached files as one `payload` field next to the files.
function generationBody(payload) {
  const { source_files: files = [], ...fields } = payload
  if (!files.length) return fields
  const body = new FormData()
  body.append('payload', JSON.stringify(fields))
  files.forEach((file) => body.append('source_files[]', file, file.name))
  return body
}

export async function generateAdWithAi(apiBase, payload, signal) {
  const response = await axios.post(`${apiBase}/ad-generation/generations`, generationBody(payload), { signal })
  return response.data.data
}

// A head start for "Generate": the agent reads the advertiser's sources now. Nothing waits for it.
export async function prepareAdGeneration(apiBase, payload, signal) {
  const response = await axios.post(`${apiBase}/ad-generation/preparations`, generationBody(payload), { signal })
  return response.data.data
}

export async function fetchGenerationStatus(apiBase, generationId, signal) {
  const response = await axios.get(`${apiBase}/ad-generation/generations/${encodeURIComponent(generationId)}`, { signal })
  return response.data.data
}

export async function fetchGenerationResult(apiBase, generationId, signal) {
  const response = await axios.get(`${apiBase}/ad-generation/generations/${encodeURIComponent(generationId)}/result`, { signal })
  return response.data.data
}

// Generated pictures are stored on Petyo, which allows the editor's origin: the browser reads
// them directly instead of the server downloading each one again. The proxy stays the fallback.
const DIRECT_IMAGE_HOSTS = new Set(['petyo.processing.smart-ads.eu'])

function directlyReadable(url) {
  try {
    return DIRECT_IMAGE_HOSTS.has(new URL(url).hostname)
  } catch {
    return false
  }
}

export async function fetchRemoteImage(apiBase, url, signal) {
  let response = null
  if (directlyReadable(url)) {
    response = await fetch(url, { mode: 'cors', credentials: 'omit', signal }).catch((error) => {
      if (signal?.aborted) throw error
      return null
    })
  }
  if (!response?.ok) {
    response = await fetch(`${apiBase}/media-library/fetch?url=${encodeURIComponent(url)}`, {
      credentials: 'same-origin', signal,
    })
  }
  if (!response.ok) throw new Error(i18n.t('editorV2.core.errors.aiImageImportFailed'))
  const blob = await response.blob()
  const name = (url.split('/').pop() || 'image').split('?')[0] || 'image'
  return new File([blob], name, { type: blob.type })
}

export async function fetchMediaLibrary(website, logos, signal) {
  const failure = i18n.t('editorV2.core.errors.libraryLoadFailed')
  const endpoint = logos
    ? `/api/studio-editor-v2/media-library/logos?website=${encodeURIComponent(website)}`
    : `/api/studio-editor-v2/media-library?website=${encodeURIComponent(website)}`
  let response
  try {
    response = await fetch(endpoint, { credentials: 'same-origin', signal })
  } catch (error) {
    if (signal?.aborted) throw error
    throw new Error(failure)
  }
  const payload = await response.json().catch(() => null)
  if (!response.ok) throw new Error(payload?.message || failure)
  const data = payload?.data || {}
  const harvested = logos ? (data.logos || []) : (data.images || [])
  return Array.isArray(harvested) ? harvested : []
}
